import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import BaseModal from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import SingleDropdown from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import Modal from 'ext-ui/components/Modal/Modal'
import Processing from 'ext-ui/components/Processing/Processing'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import AlertTriangleIcon from 'ext-ui/svg/icon/AlertTriangleIcon'
import CheckmarkCircleIcon from 'ext-ui/svg/icon/CheckmarkCircleIcon'
import { AsYouType } from 'libphonenumber-js'
import moment from 'moment-timezone'
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import SingleDateInputField from 'src/components/SingleDateInputField/SingleDateInputField'
import { useTranslate } from 'src/localization/Trns'
import {
  KybBusinessInput,
  LegalStructure,
} from 'src/queries/payments/payments.types'
import {
  KybApiFieldError,
  KybValidationError,
} from 'src/queries/payments/paymentsKybError'

import { formatPartyAddress } from './bankAccountOnboarding'
import { KybAddressFieldset } from './KybAddressFieldset'
import { KybDocumentField } from './KybDocumentField'
import { kybInvalidMessage } from './kybErrorMessages'
import { kybModalStyle } from './kybModal.style'
import { KybOwnerCard } from './KybOwnerCard'
import {
  buildKybRequest,
  digitsOnly,
  EMPTY_BENEFICIAL_OWNER,
  EMPTY_KYB_FORM_VALUES,
  isBusinessLegalStructure,
  isKybFormValid,
  KybFieldError,
  KybFormErrors,
  KybFormValues,
  validateKybForm,
} from './paymentsKyb.validation'
import { CardDetailList, CardDetailRow } from './SettingsStatusCard'

export interface KybVerificationModalProps {
  open: boolean
  onClose: () => void
  /** Submit the assembled KYB request. Resolves on success (the connection
   * refetches to KYB_PENDING); rejects to surface the error state. */
  onSubmit: (request: KybBusinessInput) => Promise<unknown>
}

type ResultState = 'loading' | 'success' | 'error' | null

const LEGAL_STRUCTURE_VALUES: { value: LegalStructure; labelKey: string }[] = [
  { value: 'llc', labelKey: 'LLC' },
  { value: 'corporation', labelKey: 'Corporation' },
  { value: 'non_profit', labelKey: 'Non-profit' },
  { value: 'partnership', labelKey: 'Partnership' },
  { value: 'sole_proprietorship', labelKey: 'Sole Proprietorship' },
  { value: 'trust', labelKey: 'Trust' },
]

/** A fresh form, seeded with the API-required single control person. */
function makeInitialValues(): KybFormValues {
  return {
    ...EMPTY_KYB_FORM_VALUES,
    beneficialOwners: [
      { ...EMPTY_BENEFICIAL_OWNER, relationshipTypes: ['control_person'] },
    ],
  }
}

/** Human-readable role list for the confirm-step summary. Only the two roles
 * the UI offers are translated; any other decode-safe role shows verbatim. */
function ownerRoleSummary(
  translate: (source: string) => string,
  owner: KybFormValues['beneficialOwners'][number],
): string {
  const roles = owner.relationshipTypes.map((role) =>
    role === 'control_person'
      ? translate('Control person')
      : role === 'beneficial_owner'
        ? translate('Beneficial owner')
        : role,
  )
  return roles.length > 0 ? roles.join(', ') : '—'
}

/**
 * Map an MT `parameter` (snake_case) carried on a 422 field error onto the form
 * field it belongs to, so a server validation error surfaces inline on the field
 * as well as in the summary banner. Params we don't recognize still appear in the
 * banner, so nothing is silently dropped.
 */
const SERVER_PARAM_TO_FIELD: { [param: string]: keyof KybFormValues } = {
  legal_business_name: 'legalBusinessName',
  legal_structure: 'legalStructure',
  date_formed: 'dateFormed',
  business_description: 'businessDescription',
  intended_use: 'intendedUse',
  expected_activity_volume: 'expectedActivityVolume',
  us_ein: 'usEin',
  ein: 'usEin',
  email: 'email',
  website: 'website',
  phone_number: 'phoneNumber',
  phone_numbers: 'phoneNumber',
  documents: 'einLetterDocument',
  identifications: 'einLetterDocument',
}

function mapServerFieldMessages(
  serverErrors: KybApiFieldError[] | null,
): Partial<Record<keyof KybFormValues, string>> {
  const map: Partial<Record<keyof KybFormValues, string>> = {}
  if (!serverErrors) return map
  serverErrors.forEach((err) => {
    const field = err.field ? SERVER_PARAM_TO_FIELD[err.field] : undefined
    if (field && !map[field]) map[field] = err.message
  })
  return map
}

/**
 * Best-effort map of a server error's `parameter` onto the step that owns it.
 * MT's exact nested parameter path (e.g. `associations[0].individual.
 * date_of_birth`) is not contractually pinned here, so we match the known
 * step-1 params first, then any substring naming an owner/association/
 * individual → step 2; unknown params fall back to step 1 (the business step).
 */
const OWNER_PARAM_PATTERN =
  /association|individual|beneficial|owner|ownership|ssn|date_of_birth|(^|_)dob(_|$)/i

function stepForServerErrors(serverErrors: KybApiFieldError[] | null): 1 | 2 {
  if (!serverErrors) return 1
  // A step-1 field wins so the user fixes the form top-down.
  const hasStep1 = serverErrors.some(
    (err) => err.field && SERVER_PARAM_TO_FIELD[err.field],
  )
  if (hasStep1) return 1
  const hasOwner = serverErrors.some(
    (err) => err.field && OWNER_PARAM_PATTERN.test(err.field),
  )
  return hasOwner ? 2 : 1
}

const STEP1_KEYS: (keyof KybFormErrors)[] = [
  'legalBusinessName',
  'legalStructure',
  'dateFormed',
  'businessDescription',
  'intendedUse',
  'expectedActivityVolume',
  'usEin',
  'email',
  'website',
  'phoneNumber',
  'businessAddress',
  'registeredAddress',
  'einLetterDocument',
]

/**
 * "Verify your business" — the multi-step KYB modal. Three steps (business +
 * EIN details → beneficial owners → confirm & submit) over the ported KYB form
 * model + validation (`paymentsKyb.validation`), submitting via
 * `useSubmitLegalEntity` (passed in as `onSubmit`). Closing mid-flow with a
 * dirty form prompts a confirm-on-exit dialog ("You'll lose all progress");
 * a clean form closes silently. Rebuilt from the prototype's `KybModal` on
 * ext-ui primitives (`BaseModal`, `Stepper`, `Modal` for the exit guard).
 */
export function KybVerificationModal({
  open,
  onClose,
  onSubmit,
}: KybVerificationModalProps): ReactElement {
  const translate = useTranslate()

  const [values, setValues] = useState<KybFormValues>(makeInitialValues)
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [result, setResult] = useState<ResultState>(null)
  const [dirty, setDirty] = useState(false)
  const [showExitConfirm, setShowExitConfirm] = useState(false)
  const [certify, setCertify] = useState(false)
  const [authorize, setAuthorize] = useState(false)
  const [agreeTerms, setAgreeTerms] = useState(false)
  // Field-level errors returned by a server 422 (`POST /payments/connection`),
  // surfaced on the form instead of the generic "something went wrong" card.
  const [serverErrors, setServerErrors] = useState<KybApiFieldError[] | null>(
    null,
  )

  // Guards a synchronous double-activation of submit (a second POST fired before
  // the first resolves); paired with disabling the control while loading.
  const submitInFlightRef = useRef(false)
  // The locked scrolling region + the step heading, for step-change scroll reset
  // and focus/announcement (a11y).
  const scrollRef = useRef<HTMLDivElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const prevStepRef = useRef(step)
  // The KYB dialog body — marked inert while the exit-confirm dialog is up so the
  // two stacked modals don't both sit in the a11y tree with competing focus.
  const kybBodyRef = useRef<HTMLDivElement>(null)

  // Render-time reset when the dialog is opened or closed — the repo's convention
  // for "reset state when an external value changes" (avoids the
  // react-hooks/set-state-in-effect lint a useEffect version would trip).
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setValues(makeInitialValues())
      setStep(1)
      setResult(null)
      setDirty(false)
      setShowExitConfirm(false)
      setCertify(false)
      setAuthorize(false)
      setAgreeTerms(false)
      setServerErrors(null)
    } else {
      // Wipe sensitive PII (EIN, SSNs, DOBs, addresses, base64 documents) the
      // instant the dialog closes — otherwise it lingers in this still-mounted
      // component after a discard or success. `result`/`step` reset on the next
      // open, so they're left here to avoid flipping the closing dialog's
      // success/loading card back to the form mid fade-out.
      setValues(makeInitialValues())
      setDirty(false)
      setCertify(false)
      setAuthorize(false)
      setAgreeTerms(false)
      setServerErrors(null)
    }
  }

  const update = (partial: Partial<KybFormValues>): void => {
    setDirty(true)
    // A stale server error no longer applies once the user edits the form.
    setServerErrors(null)
    setValues((prev) => ({ ...prev, ...partial }))
  }
  const updateOwner = (
    index: number,
    partial: Partial<KybFormValues['beneficialOwners'][number]>,
  ): void => {
    setDirty(true)
    setServerErrors(null)
    setValues((prev) => ({
      ...prev,
      beneficialOwners: prev.beneficialOwners.map((owner, i) =>
        i === index ? { ...owner, ...partial } : owner,
      ),
    }))
  }
  const addOwner = (): void => {
    setDirty(true)
    setValues((prev) => ({
      ...prev,
      beneficialOwners: [
        ...prev.beneficialOwners,
        { ...EMPTY_BENEFICIAL_OWNER },
      ],
    }))
  }
  const removeOwner = (index: number): void => {
    setDirty(true)
    setValues((prev) => ({
      ...prev,
      beneficialOwners: prev.beneficialOwners.filter((_, i) => i !== index),
    }))
  }

  // On step change: return the scroll region to the top and move focus to the
  // new step heading so it is announced (the prior step's scroll offset and
  // focus must not bleed into the next step). Skipped on first render so we do
  // not steal focus from react-modal when the dialog opens.
  useEffect(() => {
    if (prevStepRef.current === step) return
    prevStepRef.current = step
    if (scrollRef.current) scrollRef.current.scrollTop = 0
    headingRef.current?.focus()
  }, [step])

  // Mark the KYB dialog inert while the exit-confirm dialog is open — inert moves
  // focus out and hides it from the a11y tree, so the two stacked modals don't
  // fight over focus.
  useEffect(() => {
    const node = kybBodyRef.current
    if (!node) return
    if (showExitConfirm) {
      node.setAttribute('inert', '')
      node.setAttribute('aria-hidden', 'true')
    } else {
      node.removeAttribute('inert')
      node.removeAttribute('aria-hidden')
    }
  }, [showExitConfirm])

  const errors = validateKybForm(values)
  const serverFieldMessages = mapServerFieldMessages(serverErrors)
  // Prefer a local format-error message; fall back to a server field message.
  const fieldError = (
    key: keyof KybFormValues,
    code: KybFieldError | undefined,
  ): string | undefined =>
    kybInvalidMessage(translate, code) ?? serverFieldMessages[key]
  const step1Valid = STEP1_KEYS.every((key) => errors[key] === undefined)
  const step2Valid =
    values.beneficialOwners.length > 0 &&
    errors.beneficialOwners === undefined &&
    errors.ownershipTotal === undefined &&
    errors.controlPersonCount === undefined
  const allAcknowledged = certify && authorize && agreeTerms
  const step3Valid = allAcknowledged && isKybFormValid(errors)

  const doClose = (): void => {
    setShowExitConfirm(false)
    onClose()
  }
  const requestClose = (): void => {
    if (result === 'loading') return // non-dismissible in flight
    if (result === 'success') {
      doClose()
      return
    }
    if (dirty) {
      setShowExitConfirm(true)
      return
    }
    doClose()
  }

  const handleSubmit = async (): Promise<void> => {
    // Synchronous guard: a double-activation must not fire a second KYB POST
    // before the first resolves (backend idempotency is out of scope).
    if (submitInFlightRef.current) return
    submitInFlightRef.current = true
    setResult('loading')
    setServerErrors(null)
    try {
      await onSubmit(buildKybRequest(values))
      setResult('success')
    } catch (error) {
      // A 422 carries field-level errors the user can fix — surface them on the
      // form (banner + inline) and drop back onto the step that owns the failing
      // field (nested owner/DOB/address errors land on step 2, not step 1),
      // instead of the generic dead-end error card that discarded them.
      if (error instanceof KybValidationError && error.fieldErrors.length > 0) {
        setServerErrors(error.fieldErrors)
        setResult(null)
        setStep(stepForServerErrors(error.fieldErrors))
        return
      }
      setResult('error')
    } finally {
      submitInFlightRef.current = false
    }
  }

  const selectedStructure = LEGAL_STRUCTURE_VALUES.find(
    (o) => o.value === values.legalStructure,
  )

  const title =
    result === 'loading'
      ? translate('Verifying your business')
      : result === 'success'
        ? translate('Submitted for verification')
        : result === 'error'
          ? translate('Something went wrong')
          : step === 1
            ? translate('Verify your business')
            : step === 2
              ? translate('Add beneficial owners')
              : translate('Confirm details')

  const subtitle =
    result || step === 3
      ? undefined
      : step === 1
        ? translate(
            'One-time verification required before you can add a bank account.',
          )
        : translate(
            'U.S. financial regulations require us to verify anyone who owns 25% or more of this business, or who controls its finances.',
          )

  const acknowledgements: {
    key: string
    label: string
    checked: boolean
    onChange: (checked: boolean) => void
  }[] = [
    {
      key: 'certify',
      label: translate(
        'I certify that the information provided is true, accurate, and complete to the best of my knowledge.',
      ),
      checked: certify,
      onChange: setCertify,
    },
    {
      key: 'authorize',
      label: translate(
        "I authorize Extend and its banking partner, Modern Treasury, to verify this business's identity and information using the details provided above.",
      ),
      checked: authorize,
      onChange: setAuthorize,
    },
    {
      key: 'terms',
      label: translate(
        "I acknowledge and agree to Extend's Terms of Service and Modern Treasury's Platform Agreement, and consent to this information being processed for verification purposes.",
      ),
      checked: agreeTerms,
      onChange: setAgreeTerms,
    },
  ]

  const renderFooter = (): ReactElement => {
    if (result === 'success') {
      return (
        <div className="KybModal-footer single">
          <Button variant="primary" onClick={doClose} testId="kyb-done">
            {translate('Continue')}
          </Button>
        </div>
      )
    }
    if (result === 'error') {
      return (
        <div className="KybModal-footer">
          <Button variant="no-style" onClick={doClose} testId="kyb-close">
            {translate('Close')}
          </Button>
          <Button
            variant="primary"
            onClick={(): void => setResult(null)}
            testId="kyb-retry"
          >
            {translate('Try again')}
          </Button>
        </div>
      )
    }
    if (step === 1) {
      return (
        <div className="KybModal-footer">
          <Button variant="no-style" onClick={requestClose} testId="kyb-cancel">
            {translate('Cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={!step1Valid}
            onClick={(): void => setStep(2)}
            testId="kyb-next"
          >
            {translate('Next')}
          </Button>
        </div>
      )
    }
    if (step === 2) {
      return (
        <div className="KybModal-footer">
          <Button
            variant="secondary"
            onClick={(): void => setStep(1)}
            testId="kyb-back"
          >
            {translate('Back')}
          </Button>
          <Button
            variant="primary"
            disabled={!step2Valid}
            onClick={(): void => setStep(3)}
            testId="kyb-next"
          >
            {translate('Next')}
          </Button>
        </div>
      )
    }
    return (
      <div className="KybModal-footer">
        <Button
          variant="secondary"
          onClick={(): void => setStep(2)}
          testId="kyb-back"
        >
          {translate('Back')}
        </Button>
        <Button
          variant="primary"
          disabled={!step3Valid || result === 'loading'}
          onClick={handleSubmit}
          testId="kyb-submit"
        >
          {translate('Submit for verification')}
        </Button>
      </div>
    )
  }

  return (
    <>
      <BaseModal
        isOpen={open}
        onClose={requestClose}
        variant="popup"
        showCloseButton={result !== 'loading'}
        shouldCloseOnEsc={result !== 'loading'}
        shouldCloseOnOverlayClick={result !== 'loading'}
        aria-label={title}
      >
        <div className="KybModal" css={kybModalStyle} ref={kybBodyRef}>
          <div className="KybModal-header">
            <h2 className="KybModal-title" tabIndex={-1} ref={headingRef}>
              {title}
            </h2>
            {subtitle && <p className="KybModal-subtitle">{subtitle}</p>}
            {serverErrors && !result && (
              <AlertPanel variant="severe">
                <div
                  className="KybModal-serverErrors"
                  data-testid="kyb-server-errors"
                  role="alert"
                >
                  <span>
                    {translate('Please correct the following and resubmit:')}
                  </span>
                  <ul>
                    {serverErrors.map((err, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={index}>{err.message}</li>
                    ))}
                  </ul>
                </div>
              </AlertPanel>
            )}
          </div>

          <div
            className="KybModal-scroll"
            ref={scrollRef}
            role="group"
            aria-label={title}
          >
            {result === 'loading' && (
              <div className="KybModal-result" role="status" aria-live="polite">
                <Processing width={32} height={32} />
                <p className="KybModal-resultText">
                  {translate(
                    "Submitting your business's details to Modern Treasury.",
                  )}
                </p>
              </div>
            )}

            {result === 'success' && (
              <div className="KybModal-result">
                <CheckmarkCircleIcon width={48} height={48} />
                <p className="KybModal-resultTitle">
                  {translate('Business verification submitted')}
                </p>
                <p className="KybModal-resultText">
                  {translate(
                    "We're reviewing your details — usually a few minutes, up to 48 hours. We'll let you know the moment it's done.",
                  )}
                </p>
              </div>
            )}

            {result === 'error' && (
              <div className="KybModal-result">
                <AlertTriangleIcon width={40} height={40} />
                <p className="KybModal-resultText">
                  {translate(
                    'We could not submit your business details for verification. Nothing you entered was lost — try again.',
                  )}
                </p>
              </div>
            )}

            {!result && step === 1 && (
              <div className="KybModal-content step1">
                <AlertPanel
                  variant="default"
                  className="KybModal-infoPanel"
                  data-testid="kyb-documents-info"
                >
                  <div className="KybModal-infoBox">
                    <span className="KybModal-infoBoxTitle">
                      {translate('Before you start, have these ready')}
                    </span>
                    <ul className="KybModal-infoBoxList">
                      <li>
                        {translate(
                          'Your EIN and a copy of your EIN confirmation letter',
                        )}
                      </li>
                      <li>
                        {translate(
                          'Control person and beneficial owner details',
                        )}
                      </li>
                    </ul>
                  </div>
                </AlertPanel>

                {/* Section 1 — Business Identity (DBA field descoped: needs an
                    extend-api contract change — see project note). */}
                <div className="KybModal-section">
                  <h3 className="KybModal-sectionTitle">
                    {translate('Business Identity')}
                  </h3>
                  <TextInput
                    label={translate('Legal Business Name')}
                    requirement="required"
                    placeholder={translate("Add your business's legal name")}
                    value={values.legalBusinessName}
                    onChange={(e): void =>
                      update({ legalBusinessName: e.target.value })
                    }
                    errorText={fieldError(
                      'legalBusinessName',
                      errors.legalBusinessName,
                    )}
                    testId="kyb-legal-name"
                  />
                  <SingleDropdown
                    data-testid="kyb-legal-structure"
                    label={translate('Legal Structure')}
                    requirement="required"
                    placeholder={translate('Select a structure')}
                    options={LEGAL_STRUCTURE_VALUES.map((o) => ({
                      value: o.value,
                      label: translate(o.labelKey),
                    }))}
                    selectedValue={
                      selectedStructure
                        ? {
                            value: selectedStructure.value,
                            label: translate(selectedStructure.labelKey),
                          }
                        : undefined
                    }
                    onSelect={(v): void =>
                      update({ legalStructure: v.value as LegalStructure })
                    }
                    clearable={false}
                  />
                </div>

                {/* Section 2 — Tax & Registration (EIN → Date → EIN letter). */}
                <div className="KybModal-section">
                  <h3 className="KybModal-sectionTitle">
                    {translate('Tax & Registration')}
                    <TooltipIcon>
                      {translate(
                        'We verify your business against IRS records, so this must match your EIN letter exactly.',
                      )}
                    </TooltipIcon>
                  </h3>
                  <TextInput
                    label={translate('Employer Identification Number (EIN)')}
                    requirement="required"
                    placeholder={translate('Add your EIN')}
                    value={values.usEin}
                    maxLength={9}
                    onChange={(e): void =>
                      update({ usEin: digitsOnly(e.target.value).slice(0, 9) })
                    }
                    errorText={fieldError('usEin', errors.usEin)}
                    testId="kyb-ein"
                  />
                  <SingleDateInputField
                    id="kyb-date-formed"
                    required
                    fieldLabel={
                      <>
                        {translate('Date of Incorporation')}
                        <span
                          className="KybModal-requiredMark"
                          aria-hidden="true"
                        >
                          {' *'}
                        </span>
                      </>
                    }
                    allowPastAndCurrentDates
                    dateMoment={
                      values.dateFormed
                        ? moment(values.dateFormed, 'YYYY-MM-DD')
                        : undefined
                    }
                    onDateMomentChange={(m): void =>
                      update({ dateFormed: m ? m.format('YYYY-MM-DD') : '' })
                    }
                    errors={
                      fieldError('dateFormed', errors.dateFormed)
                        ? [
                            fieldError(
                              'dateFormed',
                              errors.dateFormed,
                            ) as string,
                          ]
                        : undefined
                    }
                  />
                  <KybDocumentField
                    label={translate('EIN Confirmation Letter')}
                    requirement={
                      isBusinessLegalStructure(values.legalStructure)
                        ? 'required'
                        : 'optional'
                    }
                    tooltip={translate(
                      'Upload the IRS letter (CP-575 or 147C). Must be dated within the last 6 months.',
                    )}
                    errorText={serverFieldMessages.einLetterDocument}
                    microcopy={translate(
                      'A recent IRS EIN confirmation letter or proof of address. Used for verification only — never stored.',
                    )}
                    value={values.einLetterDocument}
                    onChange={(doc): void => update({ einLetterDocument: doc })}
                    testId="kyb-ein-letter"
                  />
                </div>

                {/* Section 3 — Physical business address. */}
                <KybAddressFieldset
                  legend={translate('Physical Business Address')}
                  idPrefix="kyb-physical-address"
                  value={values.businessAddress}
                  onChange={(businessAddress): void =>
                    update({ businessAddress })
                  }
                  errors={errors.businessAddress}
                />

                {/* Section 4 — Registered address (optional to enable). */}
                <label className="KybModal-checkboxRow">
                  <Checkbox
                    aria-label={translate('Same as physical business address')}
                    checked={!values.includeRegisteredAddress}
                    onChange={(e): void =>
                      update({ includeRegisteredAddress: !e.target.checked })
                    }
                    data-testid="kyb-same-address"
                  />
                  <span>{translate('Same as physical business address')}</span>
                </label>
                {values.includeRegisteredAddress && (
                  <KybAddressFieldset
                    legend={translate('Registered Address')}
                    idPrefix="kyb-registered-address"
                    value={values.registeredAddress}
                    onChange={(registeredAddress): void =>
                      update({ registeredAddress })
                    }
                    errors={errors.registeredAddress}
                  />
                )}

                {/* Section 5 — More About Your Business (merges the old
                    description / purpose / volume + business contact fields;
                    Website dropped to match the prototype). */}
                <div className="KybModal-section">
                  <h3 className="KybModal-sectionTitle">
                    {translate('More About Your Business')}
                  </h3>
                  <TextInput
                    label={translate('What Does Your Business Do?')}
                    requirement="required"
                    placeholder={translate(
                      'e.g. Custom metal fabrication for commercial clients',
                    )}
                    value={values.businessDescription}
                    onChange={(e): void =>
                      update({ businessDescription: e.target.value })
                    }
                    errorText={fieldError(
                      'businessDescription',
                      errors.businessDescription,
                    )}
                    testId="kyb-business-description"
                  />
                  <TextInput
                    label={translate("How You'll Use This Account")}
                    requirement="required"
                    tooltipProps={{
                      children: translate(
                        "A short description of what you'll use the account for, e.g. paying suppliers and vendors.",
                      ),
                    }}
                    placeholder={translate('Paying vendors and suppliers')}
                    value={values.intendedUse}
                    onChange={(e): void =>
                      update({ intendedUse: e.target.value })
                    }
                    errorText={fieldError('intendedUse', errors.intendedUse)}
                    testId="kyb-intended-use"
                  />
                  <TextInput
                    label={translate('Expected Monthly Volume (USD)')}
                    requirement="required"
                    placeholder={translate('Add expected monthly volume')}
                    microcopy={translate(
                      'Your best estimate of total payments per month.',
                    )}
                    value={values.expectedActivityVolume}
                    onChange={(e): void =>
                      update({ expectedActivityVolume: e.target.value })
                    }
                    errorText={fieldError(
                      'expectedActivityVolume',
                      errors.expectedActivityVolume,
                    )}
                    testId="kyb-volume"
                  />
                  {/* Phone auto-formats as you type (libphonenumber AsYouType,
                      US) like the app's other phone inputs; validation and the
                      KYB request normalize the formatted value to E.164. */}
                  <TextInput
                    label={translate('Business Phone')}
                    requirement="required"
                    placeholder={translate('Add your business phone number')}
                    value={values.phoneNumber}
                    onChange={(e): void =>
                      update({
                        phoneNumber: new AsYouType('US').input(e.target.value),
                      })
                    }
                    errorText={fieldError('phoneNumber', errors.phoneNumber)}
                    testId="kyb-phone"
                  />
                  <TextInput
                    label={translate('Business Email')}
                    requirement="required"
                    placeholder={translate('Add your business email')}
                    value={values.email}
                    onChange={(e): void => update({ email: e.target.value })}
                    errorText={fieldError('email', errors.email)}
                    testId="kyb-email"
                  />
                </div>
              </div>
            )}

            {!result && step === 2 && (
              <div className="KybModal-content">
                <div className="KybModal-ownerList">
                  {values.beneficialOwners.map((owner, index) => (
                    <KybOwnerCard
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      index={index}
                      owner={owner}
                      onUpdate={(partial): void => updateOwner(index, partial)}
                      onRemove={(): void => removeOwner(index)}
                      canRemove={values.beneficialOwners.length > 1}
                      errors={errors.beneficialOwners?.[index]}
                    />
                  ))}
                </div>
                {errors.controlPersonCount && (
                  <p className="KybModal-gateNote" role="alert">
                    {translate(
                      'Exactly one person must be marked as the control person.',
                    )}
                  </p>
                )}
                {errors.ownershipTotal && (
                  <p className="KybModal-gateNote" role="alert">
                    {translate(
                      'Total ownership across all owners cannot exceed 100%.',
                    )}
                  </p>
                )}
                <div>
                  <Button
                    variant="secondary"
                    onClick={addOwner}
                    testId="kyb-add-owner"
                  >
                    {translate('Add another owner or control person')}
                  </Button>
                </div>
              </div>
            )}

            {!result && step === 3 && (
              <div className="KybModal-content">
                <div className="KybModal-summary">
                  <div className="KybModal-summaryHeader">
                    <h3 className="KybModal-summaryTitle">
                      {translate('Business details')}
                    </h3>
                    <Button
                      variant="text"
                      onClick={(): void => setStep(1)}
                      testId="kyb-edit-business"
                    >
                      {translate('Update')}
                    </Button>
                  </div>
                  <CardDetailList>
                    <CardDetailRow
                      label={translate('Legal Business Name')}
                      value={values.legalBusinessName || '—'}
                    />
                    <CardDetailRow
                      label={translate('Legal Structure')}
                      value={
                        selectedStructure
                          ? translate(selectedStructure.labelKey)
                          : '—'
                      }
                    />
                    <CardDetailRow
                      label={translate('Employer Identification Number (EIN)')}
                      value={values.usEin || '—'}
                    />
                    <CardDetailRow
                      label={translate('Physical Address')}
                      value={formatPartyAddress(values.businessAddress) || '—'}
                    />
                    <CardDetailRow
                      label={translate('Expected monthly volume')}
                      value={values.expectedActivityVolume || '—'}
                    />
                  </CardDetailList>
                </div>

                <div className="KybModal-summary">
                  <div className="KybModal-summaryHeader">
                    <h3 className="KybModal-summaryTitle">
                      {translate('Beneficial owners')}
                    </h3>
                    <Button
                      variant="text"
                      onClick={(): void => setStep(2)}
                      testId="kyb-edit-owners"
                    >
                      {translate('Update')}
                    </Button>
                  </div>
                  <CardDetailList>
                    {values.beneficialOwners.map((owner, index) => (
                      <CardDetailRow
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        label={
                          `${owner.firstName} ${owner.lastName}`.trim() ||
                          translate('Unnamed')
                        }
                        value={ownerRoleSummary(translate, owner)}
                      />
                    ))}
                  </CardDetailList>
                </div>

                <div className="KybModal-ack">
                  {acknowledgements.map((item) => (
                    <label key={item.key} className="KybModal-ackRow">
                      <Checkbox
                        aria-label={item.label}
                        checked={item.checked}
                        onChange={(e): void => item.onChange(e.target.checked)}
                        data-testid={`kyb-ack-${item.key}`}
                      />
                      <span>{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          {renderFooter()}
        </div>
      </BaseModal>

      <Modal
        open={showExitConfirm}
        onClose={(): void => setShowExitConfirm(false)}
        title={translate('Leave without saving?')}
        description={translate("You'll lose all progress.")}
        footer={
          <div className="KybModal-footer">
            <Button
              variant="secondary"
              onClick={(): void => setShowExitConfirm(false)}
              testId="kyb-exit-keep"
            >
              {translate('Keep editing')}
            </Button>
            <Button
              variant="severe"
              onClick={doClose}
              testId="kyb-exit-discard"
            >
              {translate('Leave')}
            </Button>
          </div>
        }
      />
    </>
  )
}
